import { Optional, OrderedMap, ServiceStatus } from 'services/base/types';
import { ColorCode, ColorCodeMap, PangolinDTO } from './protos/pangolin_dto_pb';
import { AvatarCode, AvatarCodeMap, UserDTO } from './protos/user_dto_pb';
import { TIMEZONE_NAME_MAP } from 'ui/standard/input/timezone_input/timezone_data';
import { UnreachableFunction } from 'ui/base/types';
import { Currency } from 'assets/enums/currencies/currencies';
import { PaymentDTO } from './protos/payment_dto_pb';
import { InvoiceDTO, InvoiceItemDTO } from './protos/invoice_dto_pb';

import {
  LicenseFirewallDTO,
  PlanFirewallDTO,
  PlansDTO,
  PriceFirewallDTO,
  PricesDTO,
  SubscriptionFirewallDTO,
} from './protos/plan_dto_pb';

export type AvatarCode = AvatarCodeMap[keyof AvatarCodeMap];
export type ColorCode = ColorCodeMap[keyof ColorCodeMap];

export type IAccountsDomain = {
  status: ServiceStatus;
  user: Optional<User>;
  pangolins: PangolinMap;
  payments: PaymentMap;
  invoices: InvoiceMap;
  prices: Optional<Prices>;
};

export type User = {
  email: string;
  unverifiedEmail: Optional<string>;
  emailVerifiedAt: Optional<Date>;
  country: string;
  mobileNumberPrefix: string;
  mobileNumber: string;
  avatarCode: AvatarCode;
  introCompleted: boolean;
  pushNotifications: boolean;
};

export enum BandwidthUnit {
  MBPS = 'mbps',
  GBPS = 'gbps',
}

export const BANDWIDTH_UNITS: BandwidthUnit[] = [
  BandwidthUnit.MBPS,
  BandwidthUnit.GBPS,
];

export type Pangolin = {
  id: string;
  publicKeyHash: string;
  nickname: string;
  colorCode: ColorCode;
  timezone: string;
  onlineStatus: boolean;
  bandwidthUploadString: Optional<string>;
  bandwidthUploadUnits: BandwidthUnit;
  bandwidthDownloadString: Optional<string>;
  bandwidthDownloadUnits: BandwidthUnit;
  enableBandwidthControls: boolean;
  enableBufferbloatPrevention: boolean;
  latency: Optional<number>;
  ipAddress: Optional<string>;
  speedtestServer: Optional<string>;
  location: Optional<string>;

  updateVersion: Optional<string>;
  updateActiveReleaseID: Optional<string>;
  updateActiveUpgradeID: Optional<string>;
  updateActiveRestoreID: Optional<string>;

  redirectModeBridgeEnabled: boolean;
  redirectModeUnicastEnabled: boolean;
  redirectModeUnicastTypeResponse: boolean;
  redirectModeUnicastTypeRequest: boolean;
  redirectModeUnicastNetworkInternet: boolean;
  redirectModeUnicastNetworkInternal: boolean;
  redirectModeBroadcastEnabled: boolean;
  redirectModeBroadcastHostGateways: boolean;
  redirectModeBroadcastHostHosts: boolean;
  redirectModeBroadcastHostAll: boolean;
  redirectModeBroadcastTypeResponse: boolean;
  redirectModeBroadcastTypeRequest: boolean;

  masqueradeEgress: boolean;

  scannerEnableScanMdns: boolean;
  scannerEnableScanNbios: boolean;
  scannerEnableScanNmap: boolean;
  scannerEnableScanRdns: boolean;
  scannerEnableScanUpnp: boolean;

  plans: Plans;
};

export class PangolinMap extends OrderedMap<string, Pangolin> {
  getDefault() {
    return this.find(p => p.onlineStatus) ?? this.getAt(0);
  }
}

// Plan

export type Plan = (
  | PlanFirewall
);

export enum PlanType {
  FIREWALL = 'firewall',
}

// eslint-disable-next-line @typescript-eslint/ban-types
export type PlanBase = {
};

export type PlanFirewall = PlanBase & {
  type: PlanType.FIREWALL;
  id: Optional<string>;

  // The feature package that the user wants to use on the next subscription
  package: Optional<PlanFirewallPackage>;

  // The billing cycle that the user wants to use on the next subscription
  billing: Optional<PlanFirewallBilling>;

  // The payment method that the user wants to use on the next subscription
  payment_id: Optional<string>;

  // A trial is a limited-time free access to a plan
  is_trialing: boolean;
  trial_until: Optional<Date>;

  // A license is the lifetime ownership of a plan
  license: Optional<LicenseFirewall>;

  // A subscription is the validity of a plan for a certain duration
  subscription: Optional<SubscriptionFirewall>;
};

export enum PlanFirewallPackage {
  BASIC = 'basic',
  BUSINESS = 'business',
  ENTERPRISE = 'enterprise',
}

export enum PlanFirewallBilling {
  MONTHLY = 'monthly',
  YEARLY = 'yearly',
  BIENNIAL = 'biennial',
  TRIENNIAL = 'triennial',
  LIFETIME = 'lifetime',
}

export type Plans = {
  firewall: Optional<PlanFirewall>;
};

// License

export type License = (
  | LicenseFirewall
);

// eslint-disable-next-line @typescript-eslint/ban-types
export type LicenseBase = {
};

export type LicenseFirewall = LicenseBase & {
  plan: PlanType.FIREWALL;
  id: Optional<string>;

  // The package that the user has a lifetime license to
  package: PlanFirewallPackage;

  // The reference to the invoice for this license
  invoice_id: Optional<string>;
};

// Subscription

export type Subscription = (
  | SubscriptionFirewall
);

// eslint-disable-next-line @typescript-eslint/ban-types
export type SubscriptionBase = {
};

export type SubscriptionFirewall = SubscriptionBase & {
  plan: PlanType.FIREWALL;
  id: Optional<string>;

  // The package that the user is subscribed to for the duration
  package: PlanFirewallPackage;

  // The duration of this subscription
  valid_since: Date;
  valid_until: Date;

  // A cancelled subscription means it is immediately ineffective
  is_cancelled: boolean;

  // A terminal subscription means it will not renew after the duration
  is_terminal: boolean;

  // An active subscription is one that is currently in effect
  is_active: boolean;

  // The reference to the invoice for this subscription
  invoice_id: Optional<string>;
};

// Price

export type Price = (
  | PriceFirewall
);

// eslint-disable-next-line @typescript-eslint/ban-types
export type PriceBase = {
};

export type PriceFirewall = PriceBase & {
  plan: PlanType.FIREWALL;
  id: Optional<string>;

  // The package that the price is for
  package: PlanFirewallPackage;

  // The billing cycle that the price is for
  billing: PlanFirewallBilling;

  // The currency and amount of the price
  currency: Currency;
  amount: number;

  // Whether the price is active
  active: boolean;
};

export type Prices = {
  firewall: PriceFirewall[];
};

// Payment

export type Payment = (
  | PaymentStripe
);

export enum PaymentType {
  STRIPE = 'stripe',
}

export type PaymentBase = {
  id: string;
  name: string;
  label: string;
};

export type PaymentStripe = PaymentBase & {
  type: PaymentType.STRIPE;
};

export class PaymentMap extends OrderedMap<string, Payment> {}
export class InvoiceMap extends OrderedMap<string, Invoice> {}

// Invoice

export type Invoice = (
  | InvoiceStripe
);

export type InvoiceBase = {
  id: string;
  label: string;
  created_at: Date;
  settled_at: Optional<Date>;
  currency: Currency;
  relations: { plan_firewall_id: Optional<string> },
  items: InvoiceItem[];
};

export type InvoiceItem = {
  label: string;
  amount: number;
  proration: boolean;
  items?: InvoiceItem[];
};

export type InvoiceStripe = InvoiceBase & {
  type: PaymentType.STRIPE;
  payment: Optional<PaymentStripe>;
  link: Optional<string>;
};

// Utility functions

export function createUserObject(userDTO: UserDTO): User {
  return {
    email: userDTO.getEmail() ?? '',
    unverifiedEmail: userDTO.getUnverifiedEmail(),
    emailVerifiedAt: userDTO.getEmailVerifiedAt()?.toDate(),
    country: userDTO.getCountry() ?? '',
    mobileNumberPrefix: userDTO.getMobileNumberPrefix() ?? '',
    mobileNumber: userDTO.getMobileNumber() ?? '',
    avatarCode: userDTO.getAvatarCode() ?? AvatarCode.ONE,
    introCompleted: userDTO.getIntroCompleted() ?? true,
    pushNotifications: userDTO.getPushNotifications() ?? true,
  };
}

export function createPlanFirewallObject(planFirewallDTO: PlanFirewallDTO): PlanFirewall {
  const licenseFirewallDTO: Optional<LicenseFirewallDTO> = planFirewallDTO.getLicense();
  const licenseFirewall: Optional<LicenseFirewall> = licenseFirewallDTO === undefined ? undefined : {
    plan: PlanType.FIREWALL,
    id: licenseFirewallDTO.getId(),
    package: licenseFirewallDTO.getPackageId()! as PlanFirewallPackage,
    invoice_id: licenseFirewallDTO.getInvoiceId() as Optional<string>,
  };

  const subscriptionFirewallDTO: Optional<SubscriptionFirewallDTO> = planFirewallDTO?.getSubscription();
  const subscriptionFirewall: Optional<SubscriptionFirewall> = subscriptionFirewallDTO === undefined ? undefined : {
    plan: PlanType.FIREWALL,
    id: subscriptionFirewallDTO.getId(),
    package: subscriptionFirewallDTO.getPackageId()! as PlanFirewallPackage,
    valid_since: subscriptionFirewallDTO.getValidSince()!.toDate(),
    valid_until: subscriptionFirewallDTO.getValidUntil()!.toDate(),
    is_cancelled: subscriptionFirewallDTO.getIsCancelled()!,
    is_terminal: subscriptionFirewallDTO.getIsTerminal()!,
    is_active: subscriptionFirewallDTO.getIsActive()!,
    invoice_id: subscriptionFirewallDTO.getInvoiceId() as Optional<string>,
  };

  const planFirewall: PlanFirewall = {
    type: PlanType.FIREWALL,
    id: planFirewallDTO.getId() as Optional<string>,
    package: planFirewallDTO.getPackageId() as Optional<PlanFirewallPackage>,
    billing: planFirewallDTO.getBillingId() as Optional<PlanFirewallBilling>,
    payment_id: planFirewallDTO.getPaymentId() as Optional<string>,
    is_trialing: planFirewallDTO.getIsTrialing() ?? false,
    trial_until: planFirewallDTO.getTrialUntil()?.toDate(),
    license: licenseFirewall,
    subscription: subscriptionFirewall,
  };

  return planFirewall;
}

export function createPlansObject(plans: Optional<PlansDTO>): Plans {
  const planFirewallDTO = plans?.getFirewall();
  return { firewall: planFirewallDTO && createPlanFirewallObject(planFirewallDTO) };
}

export function createPriceFirewallObject(priceFirewallDTO: PriceFirewallDTO): PriceFirewall {
  const priceFirewall: PriceFirewall = {
    plan: PlanType.FIREWALL,
    id: priceFirewallDTO.getId() as Optional<string>,
    package: priceFirewallDTO.getPackageId()! as PlanFirewallPackage,
    billing: priceFirewallDTO.getBillingId()! as PlanFirewallBilling,
    currency: priceFirewallDTO.getCurrency()!.toUpperCase() as Currency,
    amount: priceFirewallDTO.getAmount() ?? NaN,
    active: priceFirewallDTO.getIsActive() ?? false,
  };

  return priceFirewall;
}

export function createPriceFirewallObjects(priceFirewallDTOs: PriceFirewallDTO[]): PriceFirewall[] {
  return priceFirewallDTOs.map(createPriceFirewallObject);
}

export function createPricesObjects(prices: Optional<PricesDTO>): Prices {
  return { firewall: prices ? createPriceFirewallObjects(prices.getFirewallList()) : [] };
}

export function createPangolinObject(pangolinDTO: PangolinDTO): Pangolin {
  return {
    id: pangolinDTO.getId() ?? '',
    publicKeyHash: pangolinDTO.getPublicKeyHash() ?? '',
    nickname: pangolinDTO.getNickname() ?? 'Your Pangolin',
    colorCode: pangolinDTO.getColorCode() ?? ColorCode.RED,
    timezone: pangolinDTO.getTimezone() ?? getDefaultTimezone(),
    onlineStatus: pangolinDTO.getOnlineStatus() ?? false,
    bandwidthDownloadString: pangolinDTO.getBandwidthDownloadString(),
    bandwidthDownloadUnits: convertToBandwidthUnit(pangolinDTO.getBandwidthDownloadUnits()),
    bandwidthUploadString: pangolinDTO.getBandwidthUploadString(),
    bandwidthUploadUnits: convertToBandwidthUnit(pangolinDTO.getBandwidthDownloadUnits()),
    enableBandwidthControls: pangolinDTO.getEnableBandwidthControls() ?? false,
    enableBufferbloatPrevention: pangolinDTO.getEnableBufferbloatPrevention() ?? false,
    latency: undefined,
    ipAddress: undefined,
    speedtestServer: undefined,
    location: undefined,

    updateVersion: pangolinDTO.getMenderVersion(),
    updateActiveReleaseID: pangolinDTO.getUpdateActiveReleaseId(),
    updateActiveUpgradeID: pangolinDTO.getUpdateActiveUpgradeId(),
    updateActiveRestoreID: pangolinDTO.getUpdateActiveRestoreId(),

    redirectModeBridgeEnabled: pangolinDTO.getRedirectModeBridgeEnabled() ?? false,
    redirectModeUnicastEnabled: pangolinDTO.getRedirectModeUnicastEnabled() ?? false,
    redirectModeUnicastTypeResponse: pangolinDTO.getRedirectModeUnicastTypeResponse() ?? false,
    redirectModeUnicastTypeRequest: pangolinDTO.getRedirectModeUnicastTypeRequest() ?? false,
    redirectModeUnicastNetworkInternet: pangolinDTO.getRedirectModeUnicastNetworkInternet() ?? false,
    redirectModeUnicastNetworkInternal: pangolinDTO.getRedirectModeUnicastNetworkInternal() ?? false,
    redirectModeBroadcastEnabled: pangolinDTO.getRedirectModeBroadcastEnabled() ?? false,
    redirectModeBroadcastHostGateways: pangolinDTO.getRedirectModeBroadcastHostGateways() ?? false,
    redirectModeBroadcastHostHosts: pangolinDTO.getRedirectModeBroadcastHostHosts() ?? false,
    redirectModeBroadcastHostAll: pangolinDTO.getRedirectModeBroadcastHostAll() ?? false,
    redirectModeBroadcastTypeResponse: pangolinDTO.getRedirectModeBroadcastTypeResponse() ?? false,
    redirectModeBroadcastTypeRequest: pangolinDTO.getRedirectModeBroadcastTypeRequest() ?? false,

    masqueradeEgress: pangolinDTO.getMasqueradeEgress() ?? false,

    scannerEnableScanMdns: pangolinDTO.getScannerEnableScanMdns() ?? false,
    scannerEnableScanNbios: pangolinDTO.getScannerEnableScanNbios() ?? false,
    scannerEnableScanNmap: pangolinDTO.getScannerEnableScanNmap() ?? false,
    scannerEnableScanRdns: pangolinDTO.getScannerEnableScanRdns() ?? false,
    scannerEnableScanUpnp: pangolinDTO.getScannerEnableScanUpnp() ?? false,

    plans: createPlansObject(pangolinDTO.getPlans()),
  };
}

export function createPangolinObjects(pangolinDTOs: PangolinDTO[]): Pangolin[] {
  return pangolinDTOs.map(createPangolinObject);
}

export function pangolinSorter(a: Pangolin, b: Pangolin): number {
  // sort the Pangolins by nickname, and then by ID.
  // Sorting is intentionally only done once here, and not during modifications
  // done in pangolin_service.ts; I think dynamic changes should be kept as is.
  const aName = a.nickname.toUpperCase();
  const bName = b.nickname.toUpperCase();
  const comparison = aName.localeCompare(bName);
  if (comparison !== 0) {
    return comparison;
  }
  if (a.id < b.id) {
    return -1;
  }
  if (a.id > b.id) {
    return +1;
  }
  return 0;
}

export function createPaymentObject(paymentDTO: PaymentDTO): Payment {
  return {
    id: paymentDTO.getId() ?? '',
    type: paymentDTO.getType() as PaymentType,
    name: paymentDTO.getName() ?? '',
    label: paymentDTO.getLabel() ?? '',
  };
}

export function createInvoiceObject(invoiceDTO: InvoiceDTO): Invoice {
  const paymentType = invoiceDTO.getType() as PaymentType;
  const paymentDTO = invoiceDTO.getPayment();
  const invoiceBase: InvoiceBase = {
    id: invoiceDTO.getId() ?? '',
    label: invoiceDTO.getLabel() ?? '',
    created_at: invoiceDTO.getCreatedAt()?.toDate() ?? new Date(),
    settled_at: invoiceDTO.getSettledAt()?.toDate() ?? undefined,
    currency: invoiceDTO.getCurrency()!.toUpperCase() as Currency,
    relations: { plan_firewall_id: invoiceDTO.getRelations()?.getPlanFirewallId() },
    items: createInvoiceItemObjects(invoiceDTO.getItemsList()),
  };
  switch (paymentType) {
    case PaymentType.STRIPE:
      return {
        ...invoiceBase,
        type: PaymentType.STRIPE,
        payment: paymentDTO ? createPaymentObject(paymentDTO) : undefined,
        link: invoiceDTO.getStripeData()?.getLink() ?? undefined,
      };
    default:
      return UnreachableFunction(paymentType);
  }
}

export function createInvoiceItemObject(invoiceItemDTO: InvoiceItemDTO): InvoiceItem {
  return {
    label: invoiceItemDTO.getLabel() ?? '',
    amount: invoiceItemDTO.getAmount() ?? NaN,
    proration: invoiceItemDTO.getProration() ?? false,
    items: createInvoiceItemObjects(invoiceItemDTO.getItemsList()),
  };
}

export function createPaymentObjects(paymentDTOs: PaymentDTO[]): Payment[] {
  return paymentDTOs.map(createPaymentObject);
}

export function createInvoiceObjects(invoiceDTOS: InvoiceDTO[]): Invoice[] {
  return invoiceDTOS.map(createInvoiceObject);
}

export function createInvoiceItemObjects(invoiceItemDTOS: InvoiceItemDTO[]): InvoiceItem[] {
  return invoiceItemDTOS.map(createInvoiceItemObject);
}

export function paymentSorter(a: Payment, b: Payment): number {
  const aName = a.name.toUpperCase();
  const bName = b.name.toUpperCase();
  const nameCompare = aName.localeCompare(bName);
  if (nameCompare) return nameCompare;
  return 0;
}

export function invoiceSorter(a: Invoice, b: Invoice): number {
  const aDate = a.created_at;
  const bDate = b.created_at;
  if (aDate < bDate) return 1;
  if (aDate > bDate) return -1;
  return 0;
}

function convertToBandwidthUnit(units: Optional<string>): BandwidthUnit {
  if (units === BandwidthUnit.MBPS) {
    return BandwidthUnit.MBPS;
  } else if (units === BandwidthUnit.GBPS) {
    return BandwidthUnit.GBPS;
  }
  return BandwidthUnit.MBPS;
}

export function convertFromBandwidthUnits(units: BandwidthUnit): string {
  if (units === BandwidthUnit.MBPS) {
    return BandwidthUnit.MBPS;
  } else if (units === BandwidthUnit.GBPS) {
    return BandwidthUnit.GBPS;
  } else {
    return UnreachableFunction(units);
  }
}

function getDefaultTimezone(): string {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (TIMEZONE_NAME_MAP[tz] != null) {
      return tz;
    }
  } catch (e) {
    // Do nothing if the above fails
  }
  // TODO: Add some code to support getting the native system timezone
  // Also, try doing momentjs's guess algorithm as well
  return 'Asia/Singapore';
}
